<template>
  <transition name="alert-slide">
    <div 
      v-if="show" 
      class="glass-alert" 
      :class="[`glass-alert--${variant}`]"
      role="alert"
      :data-testid="testId || 'error-alert'"
    >
      <div class="glass-alert__icon">
        <i :class="iconClass"></i>
      </div>
      <div class="glass-alert__content">
        <strong v-if="title" class="glass-alert__title">{{ title }}</strong>
        <p class="glass-alert__message">
          <slot>{{ message }}</slot>
        </p>
      </div>
      <button v-if="dismissible" class="glass-alert__close" @click="dismiss">
        <i class="fas fa-times"></i>
      </button>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'GlassAlert',
  props: {
    show: {
      type: Boolean,
      default: true
    },
    variant: {
      type: String,
      default: 'info',
      validator: (value) => ['info', 'success', 'warning', 'danger'].includes(value)
    },
    title: {
      type: String,
      default: null
    },
    message: {
      type: String,
      default: ''
    },
    dismissible: {
      type: Boolean,
      default: false
    },
    autoDismiss: {
      type: Number,
      default: 0 // 0 = no auto dismiss
    },
    testId: {
      type: String,
      default: null
    }
  },
  computed: {
    iconClass() {
      const icons = {
        info: 'fas fa-info-circle',
        success: 'fas fa-check-circle',
        warning: 'fas fa-exclamation-triangle',
        danger: 'fas fa-exclamation-circle'
      };
      return icons[this.variant];
    }
  },
  watch: {
    show(val) {
      if (val && this.autoDismiss > 0) {
        setTimeout(() => this.dismiss(), this.autoDismiss);
      }
    }
  },
  mounted() {
    if (this.show && this.autoDismiss > 0) {
      setTimeout(() => this.dismiss(), this.autoDismiss);
    }
  },
  methods: {
    dismiss() {
      this.$emit('dismiss');
      this.$emit('update:show', false);
    }
  }
};
</script>

<style scoped>
.glass-alert {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid;
}

.glass-alert--info {
  background: rgba(59, 136, 165, 0.1);
  border-color: rgba(59, 136, 165, 0.3);
  color: white;
}

.glass-alert--success {
  background: rgba(89, 169, 106, 0.1);
  border-color: rgba(89, 169, 106, 0.3);
  color: white;
}

.glass-alert--warning {
  background: rgba(255, 193, 7, 0.1);
  border-color: rgba(255, 193, 7, 0.3);
  color: white;
}

.glass-alert--danger {
  background: rgba(195, 66, 63, 0.1);
  border-color: rgba(195, 66, 63, 0.3);
  color: white;
}

.glass-alert__icon {
  font-size: 1.25rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.glass-alert__content {
  flex: 1;
  min-width: 0;
}

.glass-alert__title {
  display: block;
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: inherit;
}

.glass-alert__message {
  margin: 0;
  color: white;
  font-size: 0.9375rem;
  line-height: 1.5;
}

.glass-alert__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: currentColor;
  opacity: 0.6;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.glass-alert__close:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.1);
}

/* Animation */
.alert-slide-enter-active,
.alert-slide-leave-active {
  transition: all 0.3s ease;
}

.alert-slide-enter,
.alert-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
